import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import DialogTransition from 'src/components/DialogTransition';
import { DatePicker } from '@mui/x-date-pickers';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import { useDispatch, useSelector } from 'react-redux';
import ContractStatus from 'src/utils/constants/contracts';
import { selectContractsList } from 'src/store/selectors/contractSelectors';
import {
  LOAD_CONTRACTS,
  loadContracts
} from 'src/store/actions/contractActions';
import { getClientFullName } from 'src/utils/common';
import {
  formatAmountWithDecimals,
  formatPositiveAmount
} from 'src/utils/formatAmout';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { loadPendingPaymentIncomeCFDIs } from 'src/store/actions/cfdiActions';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import { CREATE_AUTOMATIC_DEBIT } from 'src/store/actions/paymentsActions';
import Loading from 'src/components/Loading';
import validationSchema from './schema';
import useStyles from './styles';
import makeInitialState from './initialState';

const CreateAutomaticDebitDialog = ({ open, onClose, onSubmit }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const resetRef = useRef();
  const dispatch = useDispatch();
  const contracts = useSelector(selectContractsList);
  const [cfdiList, setCfdiList] = useState([]);
  const isLoadingContracts = useSelector(
    isLoadingSelector([LOAD_CONTRACTS.action])
  );
  const idLoadingCfdis = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );
  const isLoadingCreateAutomaticDebit = useSelector(
    isLoadingSelector([CREATE_AUTOMATIC_DEBIT.action])
  );

  const initialState = makeInitialState();

  const getContractDisplayName = contract => {
    const folio = contract.folio ?? '';
    const client = getClientFullName(contract.client) || '';
    const rent = contract.rentIvaIncluded ?? '';

    return `${t(
      'COMMON.CONTRACT'
    )} ${folio} - ${client} - $${formatPositiveAmount(rent)}`;
  };

  const getCfdiDisplayName = cfdi => {
    if (!cfdi) return '';
    return `${t('COMMON.SERIE')} ${cfdi.series} - ${
      cfdi.folio
    } - $${formatAmountWithDecimals(cfdi.amountIvaIncluded)}`;
  };

  const handleFormSubmit = values => {
    onSubmit(values);
  };

  const handleOnContractChange = useCallback(
    (value, setFieldValue, setTouched) => {
      setFieldValue('contractId', value?.id);
      setFieldValue('cfdi', '');
      setFieldValue('transactionId', '');
      setFieldValue('amount', '');
      setTouched({ cfdi: false });
      if (value) {
        dispatch(loadPendingPaymentIncomeCFDIs(value.id, setCfdiList));
      }
    },
    [dispatch, loadPendingPaymentIncomeCFDIs]
  );

  useEffect(() => {
    if (open) {
      dispatch(loadContracts({ status: ContractStatus.IN_PROGRESS }, false));
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, dispatch, open]);

  if (!open) return null;

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.dialogRoot }}
      open={open}
      keepMounted
      TransitionComponent={DialogTransition}
    >
      <DialogTitle className={classes.header}>
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={onClose}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {t('COMMON.CREATE_AUTOMATIC_DEBIT')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoadingCreateAutomaticDebit ? (
          <Loading />
        ) : (
          <Formik
            initialValues={initialState}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              errors,
              handleSubmit,
              touched,
              values,
              resetForm,
              setFieldValue,
              setTouched
            }) => {
              resetRef.current = () => resetForm();
              return (
                <Form onSubmit={handleSubmit}>
                  <Autocomplete
                    sx={{ marginTop: 2 }}
                    fullWidth
                    options={contracts}
                    getOptionLabel={getContractDisplayName}
                    onChange={(_, value) =>
                      handleOnContractChange(value, setFieldValue, setTouched)
                    }
                    loading={isLoadingContracts}
                    clearOnBlur
                    selectOnFocus={false}
                    handleHomeEndKeys={false}
                    freeSolo={false}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('COMMON.CONTRACT')}
                        variant="outlined"
                        fullWidth
                        placeholder={t('COMMON.SEARCH')}
                        error={Boolean(touched.contractId && errors.contractId)}
                        helperText={
                          Boolean(touched.contractId && errors.contractId) &&
                          t(errors.contractId)
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body1">
                          {getContractDisplayName(option)}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText={t('COMMON.NO_CONTRACTS_FOUND')}
                    loadingText={t('COMMON.LOADING')}
                  />

                  <Autocomplete
                    sx={{ marginTop: 2 }}
                    fullWidth
                    options={cfdiList}
                    getOptionLabel={getCfdiDisplayName}
                    value={values.cfdi || null}
                    onChange={(_, value) => {
                      setFieldValue('cfdi', value);
                      setFieldValue('transactionId', value.transactionId);
                    }}
                    loading={idLoadingCfdis}
                    loadingText={t('COMMON.LOADING')}
                    disabled={idLoadingCfdis || !values.contractId}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          idLoadingCfdis
                            ? t('COMMON.LOADING')
                            : t('COMMON.CFDI')
                        }
                        variant="outlined"
                        fullWidth
                        placeholder={t('COMMON.SEARCH')}
                        error={Boolean(
                          touched.transactionId && errors.transactionId
                        )}
                        helperText={
                          touched.transactionId && t(errors.transactionId)
                        }
                      />
                    )}
                    noOptionsText={t('COMMON.NO_CFDIS_FOUND')}
                  />

                  <FormControl
                    fullWidth
                    error={Boolean(touched.dueDate && errors.dueDate)}
                  >
                    <DatePicker
                      label={t('ENUMS.AUTOMATIC_DEBIT.DUE_DATE')}
                      format="DD/MM/YYYY"
                      value={values.dueDate}
                      onChange={newValue => {
                        setFieldValue('dueDate', newValue);
                      }}
                      minDate={momentLocalTimezone().startOf('month')}
                      maxDate={momentLocalTimezone().endOf('month')}
                      disabled={!values.contractId}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(touched.dueDate && errors.dueDate),
                          helperText: touched.dueDate && t(errors.dueDate),
                          sx: { marginTop: 2, marginBottom: 2 }
                        }
                      }}
                    />
                  </FormControl>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      margin: '1rem auto'
                    }}
                  >
                    <Button
                      onClick={onClose}
                      color="primary"
                      variant="outlined"
                      sx={{ minWidth: '45%' }}
                    >
                      {t('COMMON.CANCEL')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: '45%' }}
                    >
                      {t('COMMON.CREATE')}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

CreateAutomaticDebitDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default CreateAutomaticDebitDialog;
